import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { clearUserCache } from 'api/file/file-api';
import { EditNewUser, getOfficeDetails, postAddNewUser, searchFirmByDetails } from 'api/manageUsers/manage-users-api';
import { getUser, setUser } from 'api/orderManagement/order-management-api';
import clsx from 'clsx';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { defaultFscTeam, getTeamAssignmentIds, TeamAssignment } from 'features/orderManagement/enum';
import { UserType } from 'features/orderManagement/types';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useNavPermissions } from 'hooks/useNavPermissions';
import { useUnsavedNavigationGuard } from 'hooks/useUnsavedNavigationGuard';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import UnsavedChangesDialog from 'ui-kit/components/modal/UnsavedChangesDialog';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AgentNetCheckBox, AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import {
  ACTIVITY_RIGHT_LABEL_FRAUD_VALIDATION,
  ACTIVITY_RIGHT_LABEL_VIEW_AGENT_DASHBOARD_PREMIUM_REVENUE,
  ACTIVITY_RIGHT_LABEL_WIRE_VALIDATION,
  ROLES_WITH_DEFAULT_FRAUD_WIRE_VALIDATION,
} from 'utilities/constants/activity-rights';
import { formatPhoneNumber } from 'utilities/utilities';
import { AddUserSchema, EditUserSchema, EditUserSchemaForIAM } from 'utilities/validation/schemas/manage-users-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { firmStatus } from '../../../features/constants';
import AutocompleteChecklist from '../../../ui-kit/inputs/AutocompleteFirms/AutocompleteFirms'; // Import the AutocompleteChecklist component
import PhoneField from '../../../ui-kit/inputs/PhoneField/PhoneField';
import {
  USER_STATUS_ACTIVE,
  USER_STATUS_DISABLED,
  USER_STATUS_INACTIVE,
  USER_STATUS_NOT_ACTIVATED,
  USER_STATUS_TYPE_NOT_ACTIVATED,
  UserRoles,
  UserRoleTypes,
} from '../constants';
import { AddUser } from './type';
import { shouldShowUserSaveBar, useEditUserDirtyState } from './useEditUserDirtyState';
import UserBanner from './userBanner';
import { normalizeUserStatus, toUserStatusType } from './userStatus';

interface AddNewUserProps {
  activityRights: any;
  userId?: number;
  userDetail: any;
  reLoadEdit?: (userId: number) => void;
}

/** Type definition for activity rights structure returned from API */
interface ActivityRightsForRole {
  Role: string;
  DefaultRights: string[];
  AdditionalRights: string[];
}

/**
 * Computes which activity rights should be checked by default for a given role.
 * Per ANS-7533: Agent roles get Fraud + Wire validation if available.
 * All roles get "View Agent Dashboard Premium Revenue" if available.
 */
function computeDefaultRights(
  role: string | undefined,
  allActivityRights: ActivityRightsForRole[] | null,
): Set<string> {
  const defaults = new Set<string>();

  if (!role || !allActivityRights) return defaults;

  const rightsForRole = allActivityRights.find((r) => r.Role === role);
  const additionalRights = rightsForRole?.AdditionalRights ?? [];

  // Always check View Agent Dashboard if available
  if (additionalRights.includes(ACTIVITY_RIGHT_LABEL_VIEW_AGENT_DASHBOARD_PREMIUM_REVENUE)) {
    defaults.add(ACTIVITY_RIGHT_LABEL_VIEW_AGENT_DASHBOARD_PREMIUM_REVENUE);
  }

  // Check Fraud + Wire for specific roles (ANS-7533)
  if (ROLES_WITH_DEFAULT_FRAUD_WIRE_VALIDATION.has(role)) {
    if (additionalRights.includes(ACTIVITY_RIGHT_LABEL_FRAUD_VALIDATION)) {
      defaults.add(ACTIVITY_RIGHT_LABEL_FRAUD_VALIDATION);
    }
    if (additionalRights.includes(ACTIVITY_RIGHT_LABEL_WIRE_VALIDATION)) {
      defaults.add(ACTIVITY_RIGHT_LABEL_WIRE_VALIDATION);
    }
  }

  return defaults;
}

const AddNewUser = ({ activityRights, userId = 0, userDetail, reLoadEdit }: AddNewUserProps) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      fieldSection: {
        display: 'flex',
        paddingBottom: theme.spacing(2),
      },
      formWithSaveBar: {
        paddingBottom: theme.spacing(9),
      },
      fieldSectionLeft: {
        padding: theme.spacing(2, 3, 0),
      },
      sectionTitleRow: {
        display: 'flex',
        alignItems: 'center',
      },
      hqUserControl: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(2),
      },
      fieldSectionColumn: {
        marginRight: theme.spacing(200),
        padding: theme.spacing(3),
      },
      fieldSectionRight: {
        padding: theme.spacing(2, 3),
        margin: 0,
        width: '100%',
        boxSizing: 'border-box',
      },
      addFirm: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'right',
        lineHeight: 'normal',
        transition: 'none',
      },
      notes: {
        size: 'large',
        multiline: true,
      },
      tableRow: {
        borderBottom: `0px solid #E0E0E0`,
      },
      tableCell: {
        display: 'flex',
        alignItems: 'left',
      },

      textBox: {
        padding: pxToRem(12),
        marginLeft: '50px',
      },
      starsInputRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
        flexWrap: 'wrap',
      },
      expirationDate: {
        paddingTop: theme.spacing(1),
        minWidth: pxToRem(120),
      },

      errorlabel: {
        marginTop: '10px',
        marginRight: '110px',
        marginBottom: theme.spacing(1),
        whitespace: 'nowrap',
      },
      gridContainer: {
        height: 'calc(100vh - 200px) !important',
        width: '100%',
        fontFamily: theme.typography.fontFamily,
        marginLeft: theme.spacing(2.5),
      },
      rowHover: {
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
      textFormat: {
        color: 'grey',
        fontSize: '14px',
        margin: theme.spacing(0, 0, 1),
      },
      starsTableRow: {
        marginRight: '1000px',
      },
      starsTextInput: {
        width: '110px',
      },
    }),
  );

  type CheckedItems = {
    [key: string]: boolean;
  };

  interface OptionType {
    id?: string | number;
    name: string;
    default?: boolean;
    officeName?: string;
    isTitleEscrow?: boolean;
    status?: string;
    firmIdArray?: any;
    firmStatus?: string;
  }

  const history = useHistory();
  const useProfile: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile, reloadProfile } = useProfile;
  const { hasPermission } = useNavPermissions();
  const [addUser, setAddUser] = useState<AddUser>({
    FirstName: '',
    MiddleName: '',
    LastName: '',
    EmailAddress: '',
    PhoneNumber: '',
    Role: '',
    IsHqUser: false,
    Notes: '',
    Status: USER_STATUS_NOT_ACTIVATED,
    AssociatedFirmId: userFirm?.firmId ?? '',
    AssociatedOfficeId: userFirm?.offices?.find((office) => office.isDefault === true)?.officeId ?? 0,
    AdditionalRightName: [],
    LoginExpirationDays: 0,
    AdditionalFirmAccessIds: [],
  });
  const classes = useStyles();
  const adminRoles = [
    { name: 'System Administrator', value: 'System Administrator' },
    { name: 'Staff Administrator', value: 'Staff Administrator' },
    { name: 'On Behalf Of', value: 'On Behalf Of' },
    { name: 'User', value: 'User' },
    { name: 'FSC OM', value: 'FSC OM' },
  ];

  const adminAccessRoles = ['System Administrator', 'Staff Administrator', 'On Behalf Of', 'User', 'FSC OM'];

  const roles = [
    { name: 'Agent', value: 'Agent' },
    { name: 'Title & Escrow User', value: 'Title & Escrow User' },
    { name: 'Title Only User', value: 'Title Only User' },
    { name: 'Escrow Only User', value: 'Escrow Only User' },
    { name: 'Approved Attorney', value: 'Approved Attorney' },
  ];

  const AgentRole = [
    { name: 'Agent', value: 'Agent' },
    { name: 'Approved Attorney', value: 'Approved Attorney' },
  ];

  const titleEscrowRoles = [
    { name: 'Title & Escrow User', value: 'Title & Escrow User' },
    { name: 'Title Only User', value: 'Title Only User' },
    { name: 'Escrow Only User', value: 'Escrow Only User' },
    { name: 'Approved Attorney', value: 'Approved Attorney' },
  ];

  const fscTeams = [
    { name: '', value: 0 },
    ...getTeamAssignmentIds()
      .filter((id) => TeamAssignment[id] !== 'CRS')
      .map((id) => ({ name: TeamAssignment[id], value: id })),
  ];

  const firmSearchInput = {
    firmName: null,
    status: null,
    officeAddress: null,
    officeName: null,
  };
  const status = [
    { name: USER_STATUS_NOT_ACTIVATED, value: USER_STATUS_NOT_ACTIVATED },
    { name: USER_STATUS_ACTIVE, value: USER_STATUS_ACTIVE },
    { name: USER_STATUS_DISABLED, value: USER_STATUS_DISABLED },
    { name: USER_STATUS_INACTIVE, value: USER_STATUS_INACTIVE },
  ];

  const statusInactive = [
    { name: USER_STATUS_ACTIVE, value: USER_STATUS_ACTIVE },
    { name: USER_STATUS_DISABLED, value: USER_STATUS_DISABLED },
    { name: USER_STATUS_INACTIVE, value: USER_STATUS_INACTIVE },
  ];

  const statusActiveDisabled = [
    { name: USER_STATUS_ACTIVE, value: USER_STATUS_ACTIVE },
    { name: USER_STATUS_DISABLED, value: USER_STATUS_DISABLED },
  ];

  const [roleValue, setRoleValue] = useState<string>('');
  const [officeName, setOfficeName] = useState<any>(userFirm?.firmId);
  const { addSnackbarMessage } = useSnackBars();
  const [allActivityRights, setAllActivilityRights] = useState<ActivityRightsForRole[] | null>(null);
  const [defaultRights, setDefaultRights] = useState<any>(null);
  const [additionalRights, setAdditionalRights] = useState<any>(null);
  const [checkedItems, setCheckedItems] = useState<CheckedItems>({});
  const [daysToExpire, setDaysToExpire] = useState<string>('90');
  const [expirationDate, setExpirationDate] = useState<string>(); // State for the calculated expiration date
  const [error, setError] = useState<string>(''); // State for error message
  const [firmSearchModel, setFirmSearchModel] = useState<any>(firmSearchInput);
  const { getAccessToken } = useAuth();
  const [showFirm, setShowFirm] = useState<boolean>(true);

  const [firmNameArray, setfirmNameArray] = useState<any>(
    useProfile?.profile?.roleId === 3
      ? []
      : userFirm && Object.keys(userFirm).length === 0
      ? []
      : Array.isArray(userFirm?.name)
      ? userFirm?.name
      : [{ name: userFirm?.name, value: userFirm?.firmId }],
  );
  const [firmTitleValue, setFirmTitle] = useState<any>(
    firmNameArray && firmNameArray.length > 0 ? firmNameArray[0].value : null,
  );
  const [shouldShowValidation, setShouldShowValidation] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [updateUserValidationErrors, setUpdateUserValidationErrors] = useState<FieldValidationError[]>([]);
  const [officeNameArray, setOfficeNameArray] = useState<any>(
    firmNameArray.length === 0
      ? []
      : Array.isArray(userFirm?.offices)
      ? userFirm?.offices
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((office) => ({
            name: office.name,
            value: office.officeId,
          }))
      : [],
  );
  const [OfficeValue, setOfficeValue] = useState<number>(
    officeNameArray && officeNameArray.length > 0 ? officeNameArray[0]?.value : '',
  );
  const [firmId, setFirmId] = useState<any>(useProfile?.profile?.roleId === 3 ? null : userFirm?.firmId);
  const [defaultFirmId, setDefaultFirmId] = useState<any>(userDetail?.userInfo?.DefaultFirmId);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(
    firmNameArray && firmNameArray.length > 0 && useProfile?.profile?.roleId !== 3
      ? [
          {
            name: firmNameArray[0].name,
            id: firmNameArray[0].value,
            isTitleEscrow: useProfile?.profile?.firms?.filter(
              (firm: any) => firm?.firmId === firmNameArray[0].value,
            )?.[0]?.isTitleEscrow,
          },
        ]
      : [],
  );
  const mappedFirms =
    userDetail?.MultipleFirms.map((firm: any) => ({
      value: firm.FirmID,
      name: firm.FirmName,
    })) || [];
  const uniqueMappedFirms = Array.from(new Set(mappedFirms.map((firm: any) => firm.value))).map((value) =>
    mappedFirms.find((firm: any) => firm.value === value),
  );
  const [defaultFirmOptions, setDefaultFirmOptions] = useState<any>(
    Array.isArray(uniqueMappedFirms) ? uniqueMappedFirms : [],
  );
  const savedRoleName = userDetail?.userInfo?.RoleName ?? '';
  const hasSavedTitleEscrowRole = titleEscrowRoles.some((option) => option.name === savedRoleName);

  const [selectedOffice, setSelectedOffice] = useState<number>(userDetail?.userInfo?.FirmLocationId);
  const [firstName, setFirstName] = useState<string>(userDetail?.userInfo?.FirstName);
  const [middleName, setMiddleName] = useState<string>(userDetail?.userInfo?.MiddleName ?? '');
  const [lastName, setLastName] = useState<string>(userDetail?.userInfo?.LastName);
  const [email, setEmail] = useState<string>(userDetail?.userInfo?.EmailAddress);
  const [phoneNumber, setPhoneNumber] = useState<string>(userDetail?.userInfo?.PhoneNumber);
  const [isSave, setIsSave] = useState(false);

  const getLast10Digits = (phoneNumber: string): string => {
    return phoneNumber?.slice(-10);
  };

  const [actualPhoneNumber, setActualPhoneNumber] = useState<string>(
    getLast10Digits(userDetail?.userInfo?.PhoneNumber),
  );

  const [statusValue, setStatusValue] = useState<string>(normalizeUserStatus(userDetail?.userInfo?.StatusType));
  const [notes, setNotes] = useState<string>(userDetail?.userInfo?.Notes);
  const [isHqUser, setIsHqUser] = useState<boolean>(userDetail?.userInfo?.IsHQUser);
  const [LoginExpirationDays, setLoginExpirationDays] = useState<number>(
    userDetail?.userInfo?.LoginExpirationDays ? userDetail?.userInfo?.LoginExpirationDays : 90,
  );
  const assignedFirms = userDetail?.MultipleFirms.map((firm: any) => ({ firmId: firm.FirmID }));
  const [multipleFirms, setMultipleFirms] = useState<any>(assignedFirms);
  const [loginUserId, setLoginUserId] = useState<string>(userDetail?.userInfo?.LoginUserId);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [hasUnsavedCreateEdits, setHasUnsavedCreateEdits] = useState(false);
  const [hydratedEditUserDetail, setHydratedEditUserDetail] = useState<unknown>(null);
  const [hydratedEditOfficeUserId, setHydratedEditOfficeUserId] = useState<number | null>(null);
  const [addUserId, setAddUserId] = useState<number>();

  const [defaultFirm, setDefaultFirm] = useState<any>(
    userDetail?.MultipleFirms?.filter((firm: any) => firm?.IsDefaultFirm === true),
  );
  const [userRoleOptions, setuserRoleOptions] = useState<any>(
    userId && userId > 0 && (useProfile.profile?.roleId === 13 || hasPermission?.orderManagement)
      ? adminRoles
      : userId &&
        userId > 0 &&
        useProfile.profile?.roleId === 3 &&
        adminAccessRoles.includes(userDetail?.userInfo?.RoleName)
      ? adminRoles
      : userId && userId > 0
      ? defaultFirm[0]?.IsTitleEsrowFirm === true || hasSavedTitleEscrowRole
        ? titleEscrowRoles
        : AgentRole
      : userId === 0 && useProfile?.profile?.roleId === 3
      ? roles
      : userFirm?.isTitleEscrow === true
      ? titleEscrowRoles
      : AgentRole,
  );
  const [defaultRoleValue, setDefaultRoleValue] = useState<any>(
    userId === 0 && useProfile?.profile?.roleTypeId === UserRoleTypes.Agent && userFirm?.isTitleEscrow === true
      ? ''
      : userId === 0 && userRoleOptions.length === 1
      ? 'Agent'
      : '',
  );
  const [role, setRole] = useState<string>(savedRoleName);
  const [fscTeamValue, setFscTeamValue] = useState<number>(0);
  const [fscUser, setFscUser] = useState<UserType>();
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  /** Skip first edit hydration; only apply defaults when the user actually changes Role (ANS-7533). */
  const hasHydratedEditModeRef = useRef(false);
  const previousRoleRef = useRef<string | null>(null);
  const hasHydratedEditOfficeRef = useRef(false);

  useEffect(() => {
    hasHydratedEditOfficeRef.current = false;
    setHydratedEditOfficeUserId(null);
  }, [userDetail, userId]);

  // Compute which rights should be checked by default for the current role
  const defaultRightsToCheck = useMemo(
    () => computeDefaultRights(role || roleValue || defaultRoleValue, allActivityRights),
    [role, roleValue, defaultRoleValue, allActivityRights],
  );

  // Derive multipleActivityRights from checkedItems instead of storing as separate state
  // This breaks the circular dependency that caused infinite loops
  const multipleActivityRights = useMemo(() => {
    return Object.keys(checkedItems).filter((key) => checkedItems[key] && key !== 'Select All');
  }, [checkedItems]);

  const addNewUser = (field: string, value: any, markDirty = false) => {
    const newUser = { ...addUser, [field]: value } as AddUser;
    setAddUser(newUser);
    if (markDirty) {
      markCreateFormDirty();
    }
  };

  useEffect(() => {
    if (userId && userId > 0) {
      setSelectedOptions((prevOptions) => {
        const defaultUpdatedOptions = prevOptions.filter((option) => option?.id != defaultFirmId);
        const defaultfirmTitleOption = prevOptions.find((option) => option?.id === defaultFirmId);
        if (defaultfirmTitleOption) {
          defaultUpdatedOptions.unshift(defaultfirmTitleOption);
        }
        return defaultUpdatedOptions;
      });
    } else {
      setSelectedOptions((prevOptions) => {
        const updatedOptions = prevOptions.filter((option) => option.id !== firmTitleValue);
        const firmTitleOption = prevOptions.find((option) => option.id === firmTitleValue);
        if (firmTitleOption) {
          updatedOptions.unshift(firmTitleOption);
        }
        return updatedOptions;
      });
    }
  }, [firmTitleValue, defaultFirmId]);

  const formatFirmNameWithStatus = (name: string, status: string): string => {
    return status.toLowerCase() === firmStatus[1].toLowerCase() ? `${name} (${firmStatus[1]})` : name;
  };

  const handleChange = (newValue: OptionType[]) => {
    markCreateFormDirty();
    setSelectedOptions((prevOptions) => {
      const existingIds = new Set(prevOptions?.map((option) => option.id));
      const filteredNewValue = newValue.filter((option) => !existingIds.has(option.id));

      if (useProfile?.profile?.roleId === 3) {
        const updatedFilteredNewValue = filteredNewValue.map((option) => ({
          ...option,
          name: formatFirmNameWithStatus(option.name, option.firmStatus || ''),
        }));
        return prevOptions ? [...prevOptions, ...updatedFilteredNewValue] : updatedFilteredNewValue;
      } else {
        const mergedOptions = filteredNewValue.filter((option) => option.status !== USER_STATUS_INACTIVE);
        return prevOptions ? [...prevOptions, ...mergedOptions] : mergedOptions;
      }
    });
    const firmNameList = newValue.map((option) => ({
      name: formatFirmNameWithStatus(option.name, option.firmStatus || ''),
      value: option.id,
    }));
    setfirmNameArray(firmNameList);
    if (userId && userId > 0) {
      setDefaultFirmOptions((prevOptions: any) => {
        const existingIds = new Set(prevOptions.map((option: any) => option.value));
        const filteredFirmNameList = firmNameList?.filter((option: any) => !existingIds.has(option.value));
        return [...prevOptions, ...filteredFirmNameList];
      });
    }
    if (!userId && userId <= 0 && !firmTitleValue) {
      setFirmId(newValue[0]?.id);
      setOfficeValue(officeNameArray && officeNameArray.length > 0 ? officeNameArray[0]?.value : null);
      setSelectedOffice(officeNameArray && officeNameArray.length > 0 ? officeNameArray[0]?.value : null);
      setFirmTitle(firmNameArray && firmNameArray.length > 0 ? firmNameArray[0]?.value : null);
      if (selectedOptions[0]?.isTitleEscrow == false) {
        setuserRoleOptions(AgentRole);
        setDefaultRoleValue('Agent');
        setRole('Agent');
        addNewUser('Role', 'Agent');
      } else if (!selectedOptions[0] && newValue[0]?.isTitleEscrow == false) {
        setuserRoleOptions(AgentRole);
        setDefaultRoleValue('Agent');
        setRole('Agent');
        addNewUser('Role', 'Agent');
      } else {
        setuserRoleOptions(titleEscrowRoles);
        setDefaultRoleValue('');
        setRole('');
        addNewUser('Role', '');
      }
    }
  };

  useEffect(() => {
    if (!userId && userId <= 0 && !firmTitleValue) {
      setFirmTitle(firmNameArray && firmNameArray.length > 0 ? firmNameArray[0]?.value : null);
    }
  }, [firmNameArray]);

  const handleDelete = (option: OptionType) => {
    markCreateFormDirty();
    const newOptions = selectedOptions.filter((opt) => opt.id !== option.id);
    setSelectedOptions(newOptions);
    const updatedFirmNameArray = newOptions.map((opt) => ({
      name: opt.name,
      value: opt.id,
    }));
    setfirmNameArray(updatedFirmNameArray);
    setDefaultFirmOptions(updatedFirmNameArray);
  };

  const validateErrors = async (addUser: AddUser) => {
    if (addUser.AssociatedFirmId === null) {
      addUser['AssociatedFirmId'] = '';
    }
    if (getFirmsValue) {
      if (userId && userId > 0) {
        addUser['Role'] = role;
      } else {
        addUser['Role'] = defaultRoleValue;
      }
    }
    const errs: FieldValidationError[] = (await doValidate(addUser, AddUserSchema)) || [];
    await errs.forEach((error) => {
      if (error?.field === 'AssociatedOfficeId' && !addUser.AssociatedOfficeId) {
        error.message = 'Office Name Required';
      }
      if (error?.field === 'Role') {
        error.message = 'Role Required';
      }
    });
    await setValidationErrors(errs);
  };
  const validateUpdateUserErrors = async () => {
    const formattedPhoneNumber = formatPhoneNumber(actualPhoneNumber);

    const updateRequest = {
      UserId: userId > 0 ? userId : addUserId,
      FirstName: firstName,
      LastName: lastName,
      MiddleName: middleName,
      EmailAddress: email,
      PhoneNumber: formattedPhoneNumber,
      Role: role,
      UserStatus: statusValue,
      Notes: notes,
      IsHqUser: isHqUser,
      AssociatedFirmId: defaultFirmId,
      AssociatedOfficeId: selectedOffice,
      AdditionalFirmAccessIds: multipleFirms,
      LoginExpirationDays: LoginExpirationDays,
      LoginUserId: loginUserId,
      AdditionalRightName: multipleActivityRights,
    };
    let errs: FieldValidationError[];
    if (useProfile?.profile?.roleId === 13) {
      errs = (await doValidate(updateRequest, EditUserSchemaForIAM)) || [];
    } else {
      errs = (await doValidate(updateRequest, EditUserSchema)) || [];
    }

    const updatedErrs = await errs.map((err) => {
      if (err.field == 'AssociatedOfficeId') {
        err.field = 'AssociatedOfficeId';
      }
      if (err.field == 'UserStatus') {
        err.field = 'status';
      }
      if (err.field == 'AssociatedFirmId') {
        err.field = 'AssociatedFirmId';
      }
      if (err.field == 'UserStatus') {
        err.field = 'status';
      }
      if (err.field == 'Role') {
        err.message = 'Role Required';
      }
      if (
        err.field == 'EmailAddress' &&
        err.message === 'Email address cannot contain the First American domain' &&
        useProfile?.profile?.roleId === 13
      ) {
        err.message = '';
      }
      if (
        err.field == 'EmailAddress' &&
        err.message === 'Email address cannot contain the First American domain' &&
        useProfile?.profile?.roleId != 13 &&
        statusValue != USER_STATUS_NOT_ACTIVATED
      ) {
        err.message = '';
      } else {
        return err;
      }
      return err;
    });
    const errors = updatedErrs.filter((err) => err.message !== '');

    setUpdateUserValidationErrors(errors);
  };

  useEffect(() => {
    validateUpdateUserErrors();
  }, [firstName, lastName, email, phoneNumber, role, statusValue, selectedOffice, defaultFirmId]);

  useEffect(() => {
    if (userId && userId > 0) {
      const array: Array<number> = [];
      array.push(userDetail?.userInfo?.UserId);
      setDaysToExpire(userDetail?.userInfo?.LoginExpirationDays?.toString());
      setLoginExpirationDays(userDetail?.userInfo?.LoginExpirationDays);
      setStatusValue(normalizeUserStatus(userDetail?.userInfo?.StatusType));
      setDefaultFirm(userDetail?.userInfo?.DefaultFirmName);
      const defaultOfficeName =
        userId && userId > 0 && userFirm?.offices
          ? userFirm?.offices?.find((office) => office.officeId === userDetail?.userInfo?.FirmLocationId)?.name ?? ''
          : addUser.AssociatedOfficeId ?? officeName;
      setOfficeName(defaultOfficeName);
      if (useProfile?.profile?.roleId === 3 && adminAccessRoles.includes(userDetail?.userInfo?.RoleName)) {
        setShowFirm(false);
      }

      if (useProfile?.profile?.roleId === 13) {
        setShowFirm(false);
      }

      const hydratedFirmOptions = (userDetail?.MultipleFirms ?? [])
        .map((firm: any) => ({
          id: firm.FirmID,
          name: firm.FirmName,
          default: firm.IsDefaultFirm,
          isTitleEscrow: firm.IsTitleEsrowFirm,
          officeName: firm.OfficeName,
        }))
        .sort((first: OptionType, second: OptionType) => Number(!!second.default) - Number(!!first.default));
      setSelectedOptions(hydratedFirmOptions);

      const AdditionalRightName: Array<string> = Array.from(userDetail?.AdocAssignedActivityRights).map(
        (activity: any) => activity.ActivityRightName,
      );

      const initialCheckedItems: CheckedItems = AdditionalRightName?.reduce((acc, right) => {
        acc[right] = true;
        return acc;
      }, {} as CheckedItems);

      setCheckedItems(initialCheckedItems);
      setHydratedEditUserDetail(userDetail);
    }
  }, [userDetail, userId]);

  useEffect(() => {
    if (selectedOptions?.length > 0) {
      const additionalFirmsId: number[] = selectedOptions?.map((firm: any) => {
        return firm.id;
      });

      setMultipleFirms(additionalFirmsId);
    } else {
      setMultipleFirms([]);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (userId && userId === 0) {
      if (addUser) validateErrors(addUser);
    }
  }, [addUser]);

  useEffect(() => {
    if (addUser) {
      validateErrors(addUser);
    }
  }, [addUser]);

  useEffect(() => {
    // Sync multipleActivityRights (derived) to addUser.AdditionalRightName
    if (multipleActivityRights) {
      addUser.AdditionalRightName = multipleActivityRights;
    }
  }, [multipleActivityRights, addUser]);

  useEffect(() => {
    const firmIdArray = firmNameArray.map((option: any) => option.value);
    addUser.AdditionalFirmAccessIds = firmIdArray;
  }, [firmNameArray]);

  useEffect(() => {
    addUser.AssociatedOfficeId = OfficeValue;
  }, [OfficeValue]);

  useEffect(() => {
    addUser.AssociatedFirmId = firmId;
  }, [firmId]);

  const addUserRequest = async (): Promise<any> => {
    const token = await getAccessToken();
    const response = await postAddNewUser(addUser, token);
    return response;
  };

  const {
    execute: executeAddUser,
    value: addUserResponseValue,
    status: addUserResponseStatus,
    errors: addUserResponseError,
  } = useAsync<any>(addUserRequest, false);

  const editUserRequest = async (): Promise<boolean> => {
    const formattedPhoneNumber = formatPhoneNumber(actualPhoneNumber);
    const updateRequest = {
      UserId: userId > 0 ? userId : addUserId,
      FirstName: firstName,
      LastName: lastName,
      MiddleName: middleName,
      EmailAddress: email,
      PhoneNumber: formattedPhoneNumber,
      Role: role,
      UserStatus: toUserStatusType(statusValue),
      Notes: notes,
      IsHqUser: isHqUser,
      AssociatedFirmId: defaultFirmId,
      AssociatedOfficeId: selectedOffice,
      AdditionalFirmAccessIds: multipleFirms,
      LoginExpirationDays: LoginExpirationDays,
      LoginUserId: loginUserId,
      AdditionalRightName: multipleActivityRights,
    };
    const token = await getAccessToken();
    const response = await EditNewUser(updateRequest, token);
    return response;
  };

  const setFscTeamRequest = async (): Promise<UserType> => {
    const user: UserType = {
      userId: userId,
      fscRoleId: fscTeamValue,
      fscRole: TeamAssignment[fscTeamValue],
      batchLimit: fscUser?.batchLimit ?? 0,
      isActive: fscUser?.isActive ?? false,
    };
    const token = await getAccessToken();
    const response = await setUser(user, token);
    return response;
  };

  const {
    execute: executeUpdateUser,
    status: editUserResponseStatus,
    errors: editUserResponseErrors,
  } = useAsync<boolean>(editUserRequest, false);
  const { execute: executeSetFscTeam, status: executeSetFscTeamStatus } = useAsync<UserType>(setFscTeamRequest, false);

  const getOfficeList = async (firmId: any) => {
    const token = await getAccessToken();
    const response = await getOfficeDetails(token, firmId);
    const officeNames = response.Offices.map((office: any) => ({
      name: office.Name,
      value: office.OfficeId,
    }));
    setOfficeNameArray(officeNames);
    // When editing an existing user, pre-select that user's saved default office (FirmLocationId)
    // instead of always defaulting to the first office in the list (ANS-10332).
    const editingDefaultOffice =
      userId && userId > 0 && response?.Offices
        ? response.Offices.find((office: any) => office.OfficeId === userDetail?.userInfo?.FirmLocationId)
        : undefined;
    const resolvedOfficeId = editingDefaultOffice?.OfficeId ?? officeNames[0]?.value ?? null;
    const isInitialEditOfficeHydration = userId > 0 && !hasHydratedEditOfficeRef.current;

    if (isInitialEditOfficeHydration) {
      hasHydratedEditOfficeRef.current = true;
    }

    setOfficeValue(resolvedOfficeId);
    setSelectedOffice(resolvedOfficeId);

    if (isInitialEditOfficeHydration) {
      setHydratedEditOfficeUserId(userId);
    }
    if (firmId === firmNameArray[0]?.value) {
      setFirmTitle(firmNameArray && firmNameArray.length > 0 ? firmNameArray[0]?.value : null);
    }
    const officeDefault = officeNames?.find((office: any) => office.name === selectedOptions[0]?.officeName);
    const office = officeNames?.find((office: any) => office.name === selectedOptions[0]?.officeName);
    if (selectedOptions?.length === 0) {
      if (selectedOptions[0]?.isTitleEscrow) {
        addNewUser('AssociatedOfficeId', officeDefault?.value);
        addNewUser('Role', 'Title & Escrow User');
      } else {
        addNewUser('AssociatedOfficeId', officeDefault?.value);
        addNewUser('Role', 'Agent');
      }
    } else if (selectedOptions[0]?.isTitleEscrow) {
      addNewUser('AssociatedOfficeId', office?.value);
      addNewUser('Role', 'Title & Escrow User');
    } else {
      addNewUser('AssociatedOfficeId', office?.value);
      addNewUser('Role', 'Agent');
    }
    return response;
  };

  const { execute: executegetOfficeList, status: getOfficeListStatus } = useAsync<boolean>(getOfficeList, false);

  useEffect(() => {
    if (firmId && !(userId && userId > 0)) {
      executegetOfficeList(firmId).then();
      setOfficeValue(officeNameArray && officeNameArray.length > 0 ? officeNameArray[0]?.value : null);
      setSelectedOffice(officeNameArray && officeNameArray.length > 0 ? officeNameArray[0]?.value : null);
    }
  }, [firmId]);

  const selectedFirmName =
    userId > 0
      ? defaultFirmOptions.find((firm: any) => firm.value === defaultFirmId)?.name
      : firmNameArray.find((firm: any) => firm.value === firmTitleValue)?.name;

  const fetchFirms = async (): Promise<any[]> => {
    const token = await getAccessToken();
    if (!selectedFirmName) {
      return [];
    }
    firmSearchModel['firmName'] = selectedFirmName;
    await searchFirmByDetails(token, firmSearchModel);
    validateErrors(addUser);
    firmSearchModel['firmName'] = '';
    return firmSearchModel;
  };

  const getFscUser = async (): Promise<UserType> => {
    try {
      const token = await getAccessToken();
      const response = await getUser(userId, token);
      if (!getTeamAssignmentIds().includes(response.fscRoleId)) {
        throw new Error('Invalid FSC OM Role');
      }
      setFscUser(response);
      setFscTeamValue(response?.fscRoleId);
      return response;
    } catch (error) {
      // default user to display if user does not have FSC role
      const defaultUser: UserType = {
        userId: userId,
        fscRoleId: defaultFscTeam.id,
        fscRole: defaultFscTeam.team,
        batchLimit: 0,
        isActive: userDetail?.userInfo?.StatusType === USER_STATUS_ACTIVE,
      };
      setFscUser(defaultUser);
      setFscTeamValue(defaultUser.fscRoleId);
      return defaultUser;
    }
  };

  const { execute: executeGetFirms, value: getFirmsValue } = useAsync<any[]>(fetchFirms, false);
  const { execute: executeFscUser } = useAsync<UserType>(getFscUser, false);

  useEffect(() => {
    if (selectedFirmName) {
      executeGetFirms();
    }
  }, [selectedFirmName]);

  useEffect(() => {
    hasPermission?.orderManagementAdmin && executeFscUser();
  }, []);

  useEffect(() => {
    if (defaultFirmId) {
      executegetOfficeList(defaultFirmId);
      setSelectedOptions((prevOptions) => {
        const defaultUpdatedOptionsEdit = prevOptions.filter((option) => option.id !== defaultFirmId);
        const defaultfirmTitleOptionEdit = prevOptions.find((option) => option.id === defaultFirmId);
        if (defaultfirmTitleOptionEdit) {
          defaultUpdatedOptionsEdit.unshift(defaultfirmTitleOptionEdit);
        }
        return defaultUpdatedOptionsEdit;
      });
    }
  }, [defaultFirmId]);

  const handleSave = () => {
    setShouldShowValidation(true);
    if (validationErrors?.length == 0) {
      setIsSave(true);
      executeAddUser().then();
    } else {
      setIsSave(false);
      addSnackbarMessage({
        message: 'Add User Failed',
        type: 'error',
      });
    }
  };

  useEffect(() => {
    if (role && role != '') {
      RenderAtiviTyRights(role);
    }
  }, [role]);

  useEffect(() => {
    if (editUserResponseStatus === AsyncStatus.SUCCESS) {
      setNotes('');
      setHasUnsavedCreateEdits(false);
      addSnackbarMessage({
        message: 'Update Successful',
        type: 'success',
      });
      if (userId && reLoadEdit) {
        reLoadEdit(userId);
      }
      navigationGuard.proceedAfterSave();
      if (userId === profile?.userId) {
        const refreshProfile = async () => {
          if (profile?.login) {
            try {
              const token = await getAccessToken();
              await clearUserCache(profile.login, token);
              reloadProfile && reloadProfile();
            } catch (error) {
              console.error('Error clearing user cache:', error);
            }
          }
        };

        refreshProfile();
      }
    }
  }, [editUserResponseStatus]);

  useEffect(() => {
    if (editUserResponseStatus === AsyncStatus.ERROR) {
      addSnackbarMessage({
        message: editUserResponseErrors[0] || 'Update User Failed',
        type: 'error',
      });
    }
  }, [editUserResponseErrors, editUserResponseStatus]);

  const handleUpdate = () => {
    if (hasPermission?.orderManagement) {
      executeSetFscTeam().then(() => {
        history.push('/admin/users/manage', { userId: userId });
      });
    } else {
      setShouldShowValidation(true);
      validateUpdateUserErrors();
      if (updateUserValidationErrors?.length === 0) {
        executeUpdateUser();
      } else {
        addSnackbarMessage({
          message: 'Update User Failed',
          type: 'error',
        });
      }
    }
  };

  const selectAll = 'Select All';
  const disableFeature: boolean =
    (useProfile?.profile?.roleId === 3 || useProfile?.profile?.roleId === 14) &&
    userId &&
    userId > 0 &&
    adminAccessRoles.includes(role)
      ? true
      : false;

  const HasUserAndIsAdminAccessRoleExceptOrderManagementAdminActivityRight: boolean =
    userId && userId > 0 && adminAccessRoles.includes(role) && !hasPermission?.orderManagementAdmin ? true : false;

  const currentEditKey = useMemo(() => {
    return JSON.stringify({
      firstName: firstName ?? '',
      middleName: middleName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      phone: actualPhoneNumber ?? '',
      role: role ?? '',
      status: statusValue ?? '',
      notes: notes ?? '',
      isHqUser: !!isHqUser,
      office: selectedOffice ?? null,
      firm: defaultFirmId ?? null,
      firms: [...(multipleFirms ?? [])]
        .map((firm: any) => (firm && typeof firm === 'object' ? firm.firmId : firm))
        .sort(),
      rights: [...(multipleActivityRights ?? [])].sort(),
      loginExpirationDays: LoginExpirationDays || 90,
    });
  }, [
    firstName,
    middleName,
    lastName,
    email,
    actualPhoneNumber,
    role,
    statusValue,
    notes,
    isHqUser,
    selectedOffice,
    defaultFirmId,
    multipleFirms,
    multipleActivityRights,
    LoginExpirationDays,
  ]);

  const currentCreateKey = useMemo(
    () =>
      JSON.stringify({
        firstName: addUser.FirstName ?? '',
        middleName: addUser.MiddleName ?? '',
        lastName: addUser.LastName ?? '',
        email: addUser.EmailAddress ?? '',
        phone: addUser.PhoneNumber ?? '',
        role: defaultRoleValue ?? '',
        status: addUser.Status ?? USER_STATUS_NOT_ACTIVATED,
        notes: addUser.Notes ?? '',
        isHqUser: !!addUser.IsHqUser,
        AssociatedFirmId: firmTitleValue ?? addUser.AssociatedFirmId,
        AssociatedOfficeId: OfficeValue ?? addUser.AssociatedOfficeId,
        AdditionalFirmAccessIds: [...(selectedOptions ?? [])]
          .map((firm) => firm.id)
          .filter((firmId) => firmId != null)
          .sort(),
        AdditionalRightName: [...(multipleActivityRights ?? [])].sort(),
        loginExpirationDays: LoginExpirationDays || 90,
        fscTeamValue,
      }),
    [
      addUser.FirstName,
      addUser.MiddleName,
      addUser.LastName,
      addUser.EmailAddress,
      addUser.PhoneNumber,
      addUser.Status,
      addUser.Notes,
      addUser.IsHqUser,
      addUser.AssociatedFirmId,
      addUser.AssociatedOfficeId,
      defaultRoleValue,
      firmTitleValue,
      OfficeValue,
      selectedOptions,
      multipleActivityRights,
      LoginExpirationDays,
      fscTeamValue,
    ],
  );
  const createBaselineKeyRef = useRef(currentCreateKey);

  function markCreateFormDirty() {
    if (!(userId && userId > 0)) {
      if (!hasUnsavedCreateEdits) {
        createBaselineKeyRef.current = currentCreateKey;
      }
      setHasUnsavedCreateEdits(true);
    }
  }

  useEffect(() => {
    if (!hasUnsavedCreateEdits && !isSaved) {
      createBaselineKeyRef.current = currentCreateKey;
    }
  }, [currentCreateKey, hasUnsavedCreateEdits, isSaved]);
  const isSavedNewUserDirty = hasUnsavedCreateEdits && createBaselineKeyRef.current !== currentCreateKey;

  const isEditHydrated =
    hydratedEditUserDetail === userDetail &&
    (!defaultFirmId || hydratedEditOfficeUserId === userId || getOfficeListStatus === AsyncStatus.ERROR);
  const isEditDirty = useEditUserDirtyState({
    userId,
    userDetail,
    currentEditKey,
    isHydrated: isEditHydrated,
    shouldResetBaseline: editUserResponseStatus === AsyncStatus.SUCCESS,
  });
  // Add User keeps the save bar visible on load. Edit User only shows it after a change,
  // except for FSC OM admins, which use a distinct always-visible save flow.
  const showSaveBar = shouldShowUserSaveBar({
    isExistingUser: userId > 0,
    isOrderManagementAdmin: !!hasPermission?.orderManagementAdmin,
    isEditDirty,
    isSavedNewUser: isSaved,
    isSavedNewUserDirty,
  });
  const isNavigationDirty = userId > 0 ? isEditDirty : isSavedNewUserDirty;
  const navigationGuard = useUnsavedNavigationGuard({
    isDirty: isNavigationDirty,
    onSave: () => {
      if (isSaved || userId > 0) {
        handleUpdate();
      } else {
        handleSave();
      }
    },
  });

  useEffect(() => {
    // Initialize checkedItems directly from userDetail instead of going through multipleActivityRights
    const initialCheckedItems =
      userDetail?.AdocAssignedActivityRights?.reduce((acc: CheckedItems, activity: any) => {
        acc[activity.ActivityRightName] = true;
        return acc;
      }, {} as CheckedItems) ?? {};
    setCheckedItems(initialCheckedItems);
    if (activityRights) {
      setAllActivilityRights(activityRights);
      if (userId && userId > 0) {
        const agentRights = activityRights.filter((right: any) => right.Role === userDetail?.userInfo?.RoleName);
        const deafultsRights = agentRights[0]?.DefaultRights?.sort((a: any, b: any) => a.localeCompare(b));
        if (deafultsRights) {
          setDefaultRights(deafultsRights);
          splitListIntoColumns(deafultsRights);
        }
        const additionalRights = agentRights[0]?.AdditionalRights?.sort((a: any, b: any) => a.localeCompare(b));
        if (additionalRights) {
          setAdditionalRights(additionalRights);
          splitListIntoColumns(additionalRights);
        }
      } else {
        const agentRights = activityRights.filter((right: any) => right.Role === 'Agent');
        const deafultsRights = agentRights[0]?.DefaultRights?.sort((a: any, b: any) => a.localeCompare(b));
        if (deafultsRights) {
          setDefaultRights(deafultsRights);
          splitListIntoColumns(deafultsRights);
        }
        const additionalRights = agentRights[0]?.AdditionalRights?.sort((a: any, b: any) => a.localeCompare(b));
        if (additionalRights) {
          setAdditionalRights(additionalRights);
          splitListIntoColumns(additionalRights);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (addUserResponseValue) {
      addSnackbarMessage({
        message: `Account activation was sent to ${addUserResponseValue.EmailAddress}.`,
        type: 'success',
      });
      const formattedPhoneNumber = formatPhoneNumber(addUserResponseValue.PhoneNumber);
      setAddUserId(addUserResponseValue.UserId);
      setIsSaved(true);
      setHasUnsavedCreateEdits(false);
      setIsSave(false);
      setAddUserId(addUserResponseValue.UserId);
      setEmail(addUserResponseValue.EmailAddress);
      setPhoneNumber(formattedPhoneNumber);
      setRole(addUserResponseValue.RoleType);
      setStatusValue(addUser.Status);
      setLoginUserId(addUserResponseValue.LoginUserId);
      setNotes(addUser.Notes);
      setIsHqUser(addUserResponseValue.IsHqUser);
      setSelectedOffice(addUserResponseValue.DefaultOfficeId);
      setDefaultFirmId(addUserResponseValue.DefaultFirmId);
      setLoginExpirationDays(addUserResponseValue.LoginExpirationDays);
      setMultipleFirms(addUser.AdditionalFirmAccessIds);
      // Initialize checkedItems from saved activity rights (multipleActivityRights is now derived)
      const savedCheckedItems =
        addUser.AdditionalRightName?.reduce((acc: CheckedItems, right: string) => {
          acc[right] = true;
          return acc;
        }, {} as CheckedItems) ?? {};
      setCheckedItems(savedCheckedItems);
      setDaysToExpire(addUserResponseValue.LoginExpirationDays?.toString());
    }
  }, [addUserResponseValue]);

  useEffect(() => {
    if (addUserResponseStatus === AsyncStatus.SUCCESS) {
      setIsSaved(true);
      navigationGuard.proceedAfterSave();
    }
  }, [addUserResponseStatus]);

  useEffect(() => {
    if (addUserResponseError.length > 0) {
      addSnackbarMessage({
        message: 'Add User Failed',
        type: 'error',
      });
      setIsSave(false);
    }
  }, [addUserResponseError]);

  const splitListIntoColumns = (list: any) => {
    const part1: any = [];
    const part2: any = [];

    list?.forEach((item: any, index: any) => {
      if (index % 2 === 0) {
        part1.push(item);
      } else {
        part2.push(item);
      }
    });

    return [part1, part2];
  };

  const [leftColumnAdditional, rightColumnAdditional] = splitListIntoColumns(additionalRights);

  const RenderAtiviTyRights = (role: string) => {
    if (userId === 0) {
      setCheckedItems({});
      setDaysToExpire('90');
      setLoginExpirationDays(90);
    }
    const agentRights = allActivityRights?.filter((right) => right.Role === role);
    if (agentRights && agentRights.length > 0) {
      const deafultsRights = agentRights[0]?.DefaultRights?.sort((a, b) => a.localeCompare(b));
      if (deafultsRights) {
        setDefaultRights(deafultsRights);
      }
      const additionalRights = agentRights[0]?.AdditionalRights?.sort((a, b) => a.localeCompare(b));
      if (additionalRights) {
        setAdditionalRights(additionalRights);
      }
    }
  };

  /**
   * Reset refs when userId changes (switching between add/edit or different user).
   */
  useEffect(() => {
    hasHydratedEditModeRef.current = false;
    previousRoleRef.current = null;
  }, [userId]);

  /**
   * Apply default activity rights based on role selection.
   * Add mode: Apply defaults whenever role changes
   * Edit mode: Apply defaults only when user actually changes role (not on initial hydration)
   */
  useEffect(() => {
    // Guard: Wait for data to load
    if (!allActivityRights || !role) return;

    const isAddMode = userId === 0;
    const isEditMode = userId > 0;

    // Add Mode: Always apply defaults when role or data changes
    if (isAddMode) {
      setCheckedItems((prev) => {
        const next: CheckedItems = { ...prev };

        // Apply computed defaults
        defaultRightsToCheck.forEach((rightName) => {
          next[rightName] = true;
        });

        return next;
      });
      return;
    }

    // Edit Mode: More complex - preserve existing selections, apply defaults only on role change
    if (isEditMode) {
      // First render in edit mode: hydrate from existing data, don't apply defaults
      if (!hasHydratedEditModeRef.current) {
        hasHydratedEditModeRef.current = true;
        previousRoleRef.current = role;
        return;
      }

      // Role hasn't changed: no-op
      if (previousRoleRef.current === role) {
        return;
      }

      // Role changed: apply defaults + preserve compatible selections
      previousRoleRef.current = role;

      const rightsForNewRole = allActivityRights.find((r) => r.Role === role);
      const availableRights = rightsForNewRole?.AdditionalRights ?? [];

      if (availableRights.length === 0) return;

      // Preserve existing selections that are still available
      const previousSelections = new Set(multipleActivityRights ?? []);

      setCheckedItems(() => {
        const next: CheckedItems = {};

        // Start with available rights that were previously selected
        availableRights.forEach((rightName) => {
          next[rightName] = previousSelections.has(rightName);
        });

        // Apply computed defaults (will override to true if already false)
        defaultRightsToCheck.forEach((rightName) => {
          if (availableRights.includes(rightName)) {
            next[rightName] = true;
          }
        });

        return next;
      });
    }
  }, [userId, role, allActivityRights, defaultRightsToCheck]);

  const handleCheckBoxClick = (name: string) => {
    markCreateFormDirty();
    const daysToAdd = daysToExpire;
    const currentDateTimePst = moment().tz('America/Los_Angeles');
    const futureDateTimePst = currentDateTimePst.add(daysToAdd, 'days');
    const formattedDate = futureDateTimePst.format('MM/DD/YYYY');

    const daysNumber = parseInt(daysToExpire, 10);
    if (daysNumber || daysNumber === 0 || daysNumber >= 90) {
      const event = { target: { value: '90' } } as React.ChangeEvent<HTMLInputElement>;
      handleDaysToExpireChange(event);
      setError('');
    }

    setExpirationDate(formattedDate);
    if (name === selectAll) {
      const newCheckedItems = { ...checkedItems };
      const allChecked = !selectAllChecked;
      leftColumnAdditional?.forEach((right: any) => {
        newCheckedItems[right] = allChecked;
      });
      rightColumnAdditional?.forEach((right: any) => {
        newCheckedItems[right] = allChecked;
      });
      setCheckedItems(newCheckedItems);
    } else {
      setCheckedItems((prevState: any) => ({
        ...prevState,
        [name]: !prevState[name],
      }));
    }
  };

  useEffect(() => {
    const allChecked =
      leftColumnAdditional?.every((right: any) => checkedItems[right]) &&
      rightColumnAdditional?.every((right: any) => checkedItems[right]);
    setSelectAllChecked(allChecked);
  }, [checkedItems, leftColumnAdditional, rightColumnAdditional]);

  const handleDaysToExpireChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    markCreateFormDirty();
    const value = parseInt(event.target.value, 10);
    setDaysToExpire(event.target.value);
    setLoginExpirationDays(value);
    addNewUser('LoginExpirationDays', value);
    if (value > 0 && value <= 90) {
      setError(''); // Clear error if value is valid
      const daysToAdd = value;
      const currentDateTimePst = moment().tz('America/Los_Angeles');
      const futureDateTimePst = currentDateTimePst.add(daysToAdd, 'days');
      const formattedDate = futureDateTimePst.format('MM/DD/YYYY');
      setExpirationDate(formattedDate);
    } else if (value <= 0) {
      setError('Days to expire must be greater than 0');
    } else if (value > 90) {
      setError('Days to expire must be less than or equal to 90');
    } else {
      setExpirationDate('');
      setError('Days to expire Required');
    }
  };

  const starsStatusOverrideInput = () => (
    <div className={classes.starsInputRow}>
      <div>
        <AgentNetTextInput
          fullWidth
          variant="outlined"
          label="Days to Expire"
          name="starsStatusOverride"
          value={daysToExpire}
          required
          data-testid="UserProfilePhoneEdit"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            if (value === '' || (Number(value) <= 99 && /^[0-9]*$/.test(value))) {
              handleDaysToExpireChange(event);
              addNewUser('LoginExpirationDays', value);
            }
          }}
          inputProps={{
            pattern: '[0-9]*',
            type: 'text',
            max: 99,
            onKeyPress: (event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            },
          }}
          className={classes.starsTextInput}
        />
        {error && (
          <Typography color="error" variant="body1" className={clsx(classes.starsTextInput)}>
            {error}
          </Typography>
        )}
      </div>
      <div className={classes.expirationDate}>
        <Typography variant="body2" color="textSecondary">
          Expiration Date
        </Typography>
        <Typography variant="body1">{expirationDate || ''}</Typography>
      </div>
    </div>
  );

  const renderActivityRights = () => (
    <>
      <Grid item sm={12} className={classes.fieldSectionLeft}>
        <Typography variant="h2">Activity Rights</Typography>
      </Grid>
      <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
        <Grid item sm={12}>
          <h3 className={clsx(classes.textFormat)}>DEFAULT RIGHTS</h3>
          <Grid container spacing={2}>
            {defaultRights?.map((right: any, i: any) => (
              <Grid item xs={12} sm={3} key={`default-${i}`} className={classes.tableCell}>
                <AgentNetCheckBox disabled value label={right} name={`default-${right}`} />
              </Grid>
            ))}
          </Grid>
        </Grid>
        {additionalRights && additionalRights.length > 0 && (
          <Grid item sm={12}>
            <h3 className={clsx(classes.textFormat)}>ADDITIONAL RIGHTS</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3} className={classes.tableCell}>
                <AgentNetCheckBox
                  label={selectAll}
                  name={selectAll}
                  disabled={disableFeature}
                  value={selectAllChecked}
                  checkHandler={() => handleCheckBoxClick(selectAll)}
                />
              </Grid>
              {additionalRights.map((right: any, i: any) => (
                <Grid item xs={12} sm={3} key={`additional-${i}`} className={classes.tableCell}>
                  <AgentNetCheckBox
                    label={right}
                    name={right}
                    disabled={disableFeature}
                    value={!!checkedItems[right]}
                    checkHandler={() => handleCheckBoxClick(right)}
                  />
                </Grid>
              ))}
              {checkedItems['STARS Status Override'] && (
                <Grid item xs={12}>
                  {starsStatusOverrideInput()}
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );

  const actionStatus =
    editUserResponseStatus === AsyncStatus.PENDING || addUserResponseStatus === AsyncStatus.PENDING
      ? AsyncStatus.PENDING
      : AsyncStatus.IDLE;
  const isSystemAdmin = useProfile?.profile?.roleId === UserRoles.SystemAdministrator;
  const isStaffAdmin = useProfile?.profile?.roleId === UserRoles.StaffAdministrator;
  const isAgentNetAdmin = !isSystemAdmin && !isStaffAdmin && !!hasPermission?.agentNetAdmin;
  const showHqUser = isSystemAdmin;
  const isCurrentUserHq = userId && userId > 0 ? !!isHqUser : !!addUser.IsHqUser;
  const availableFirmOptions = userId && userId > 0 ? defaultFirmOptions : firmNameArray;
  const hasFirmOptions = (availableFirmOptions?.length ?? 0) > 0;
  const hasOfficeOptions = (officeNameArray?.length ?? 0) > 0;
  const canToggleHqUser = showHqUser && selectedOptions.length > 0;

  return (
    <>
      <LoadingSpinner status={actionStatus} variant="linear" />
      {isSaved || (userId && userId > 0) ? (
        <>
          <UserBanner
            firstName={firstName}
            lastName={lastName}
            middleName={userDetail?.userInfo?.MiddleName}
            userId={userDetail?.userInfo?.loginUserId == undefined ? loginUserId.toString() : ''}
            id={userId && userId > 0 ? userId : addUserResponseValue.UserId}
            email={userId && userId > 0 ? email : addUser.EmailAddress}
            roleType={
              userDetail?.userInfo?.RoleTypeCdId > 0
                ? userDetail.userInfo.RoleTypeCdId
                : addUserResponseValue.RoleTypeId
            }
            userStatus={
              userDetail?.userInfo?.StatusType === USER_STATUS_TYPE_NOT_ACTIVATED
                ? USER_STATUS_NOT_ACTIVATED
                : userDetail?.userInfo?.StatusType || addUser.Status
            }
            onBack={() => navigationGuard.requestNavigation(() => window.location.reload())}
          />
          {/* <AgentNetDivider typoVariant="h1" title={`Edit User:` + '  ' + firstName + lastName} />
          <AgentNetDivider typoVariant="h4" title={`UserId:` + '  ' + userDetail?.userInfo?.LoginUserId} /> */}
        </>
      ) : (
        <PageHeader
          title="Add User"
          subtitle="Enter new user details and configure their access permissions and activity rights."
        />
      )}
      <Grid container className={clsx(classes.fieldSection, showSaveBar && classes.formWithSaveBar)}>
        <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              label={'First Name'}
              name="FirstName"
              data-testid="firstName"
              value={userId && userId > 0 ? firstName : addUser.FirstName}
              onChange={(event: any) => {
                addNewUser('FirstName', event.target.value, true);
                setFirstName(event.target.value);
              }}
              required
              errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
              showValidation={shouldShowValidation}
              disabled={
                userId &&
                userId > 0 &&
                useProfile?.profile?.roleId === 3 &&
                userDetail?.userInfo?.RoleTypeName === 'Staff'
                  ? true
                  : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              label={'Middle Name'}
              name="MiddleName"
              data-testid="middleName"
              value={userId && userId > 0 ? middleName : addUser.MiddleName}
              onChange={(event: any) => {
                addNewUser('MiddleName', event.target.value, true);
                setMiddleName(event.target.value);
              }}
              disabled={
                userId &&
                userId > 0 &&
                useProfile?.profile?.roleId === 3 &&
                userDetail?.userInfo?.RoleTypeName === 'Staff'
                  ? true
                  : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              label={'Last Name'}
              name="LastName"
              data-testid="lastName"
              value={userId && userId > 0 ? lastName : addUser.LastName}
              onChange={(event: any) => {
                addNewUser('LastName', event.target.value, true);
                setLastName(event.target.value);
              }}
              required
              errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
              showValidation={shouldShowValidation}
              disabled={
                userId &&
                userId > 0 &&
                useProfile?.profile?.roleId === 3 &&
                userDetail?.userInfo?.RoleTypeName === 'Staff'
                  ? true
                  : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item sm={3}>
            {userId && userId === 0 ? (
              <PhoneField
                fullWidth
                variant="outlined"
                label="Phone Number"
                name="PhoneNumber"
                value={userId && userId > 0 ? actualPhoneNumber : addUser.PhoneNumber}
                required
                data-testid="phoneNumber"
                onChange={(event: any) => {
                  addNewUser('PhoneNumber', event.target.value, true);
                  setActualPhoneNumber(event.target.value);
                  setPhoneNumber(event.target.value);
                }}
                errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
                showValidation={shouldShowValidation}
                disabled={
                  userId &&
                  userId > 0 &&
                  useProfile?.profile?.roleId === 3 &&
                  userDetail?.userInfo?.RoleTypeName === 'Staff'
                    ? true
                    : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                    ? true
                    : false
                }
              />
            ) : userId && userId > 0 && userDetail?.userInfo?.StatusType !== USER_STATUS_TYPE_NOT_ACTIVATED ? (
              <AgentNetTextInput
                fullWidth
                variant="outlined"
                label="Phone Number"
                name="PhoneNumber"
                value={userId && userId > 0 ? userDetail?.userInfo?.PhoneNumber : addUser.PhoneNumber}
                required
                data-testid="phoneNumber"
                onChange={(event: any) => {
                  addNewUser('PhoneNumber', event.target.value, true);
                  setActualPhoneNumber(event.target.value);
                  setPhoneNumber(event.target.value);
                }}
                errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
                showValidation={shouldShowValidation}
                disabled={
                  userId &&
                  userId > 0 &&
                  useProfile?.profile?.roleId === 3 &&
                  userDetail?.userInfo?.RoleTypeName === 'Staff'
                    ? true
                    : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                    ? true
                    : false
                }
              />
            ) : (
              <PhoneField
                fullWidth
                variant="outlined"
                label="Phone Number"
                name="PhoneNumber"
                value={userId && userId > 0 ? actualPhoneNumber : addUser.PhoneNumber}
                required
                data-testid="phoneNumber"
                onChange={(event: any) => {
                  addNewUser('PhoneNumber', event.target.value, true);
                  setActualPhoneNumber(event.target.value);
                  setPhoneNumber(event.target.value);
                }}
                errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
                showValidation={shouldShowValidation}
                disabled={
                  userId &&
                  userId > 0 &&
                  useProfile?.profile?.roleId === 3 &&
                  userDetail?.userInfo?.RoleTypeName === 'Staff'
                    ? true
                    : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                    ? true
                    : false
                }
              />
            )}
          </Grid>
          <Grid item sm={6}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              label={'Email'}
              name="EmailAddress"
              value={userId && userId > 0 ? email : addUser.EmailAddress}
              required
              onChange={(event: any) => {
                addNewUser('EmailAddress', event.target.value, true);
                setEmail(event.target.value);
              }}
              errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
              showValidation={shouldShowValidation}
              disabled={
                userId &&
                userId > 0 &&
                useProfile?.profile?.roleId === 3 &&
                userDetail?.userInfo?.RoleTypeName === 'Staff'
                  ? true
                  : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetDropdownSelector
              fullWidth
              disabled={disableFeature}
              variant="outlined"
              label={'Role'}
              name="Role"
              value={userId && userId > 0 ? role : defaultRoleValue}
              required
              data-testid="role"
              options={userRoleOptions}
              menuOption={(value: string) => {
                addNewUser('Role', value, true);
                setRoleValue(value);
                RenderAtiviTyRights(value);
                setRole(value);
                setDefaultRoleValue(value);
              }}
              errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
              showValidation={shouldShowValidation}
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetDropdownSelector
              fullWidth
              variant="outlined"
              label={'Status'}
              name="status"
              value={
                userId && userId > 0
                  ? statusValue === USER_STATUS_NOT_ACTIVATED
                    ? USER_STATUS_NOT_ACTIVATED
                    : statusValue === USER_STATUS_INACTIVE
                    ? USER_STATUS_INACTIVE
                    : statusValue
                  : addUser.Status ?? USER_STATUS_NOT_ACTIVATED
              }
              disabled={
                disableFeature ? true : userId && userId > 0 && statusValue !== USER_STATUS_NOT_ACTIVATED ? false : true
              }
              defaultValue={status[0]?.value}
              data-testid="status"
              options={
                userId && userId > 0 && (statusValue === USER_STATUS_ACTIVE || statusValue === USER_STATUS_DISABLED)
                  ? statusActiveDisabled
                  : userId && userId > 0 && statusValue === USER_STATUS_INACTIVE
                  ? statusInactive
                  : status
              }
              menuOption={(value: string) => {
                addNewUser('Status', value, true);
                setStatusValue(value);
              }}
              errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
              showValidation={shouldShowValidation}
            />
          </Grid>
          {hasPermission?.orderManagementAdmin && (
            <Grid item sm={6}>
              <AgentNetDropdownSelector
                fullWidth
                variant="outlined"
                label={'FSC OM Team'}
                name="FscRole"
                value={fscTeamValue || ''}
                data-testid="fsc-role"
                options={fscTeams}
                menuOption={(value: number) => {
                  setFscTeamValue(value);
                  markCreateFormDirty();
                }}
              />
            </Grid>
          )}
        </Grid>
        {showFirm && !hasPermission?.orderManagementAdmin && (
          <>
            <Grid item sm={12} className={classes.fieldSectionLeft}>
              <div className={classes.sectionTitleRow}>
                <Typography variant="h2">Defaults</Typography>
                {showHqUser && (
                  <div className={classes.hqUserControl}>
                    <AgentNetCheckBox
                      label="HQ User"
                      value={isCurrentUserHq}
                      disabled={!canToggleHqUser}
                      checkHandler={(val: boolean) => {
                        setIsHqUser(val);
                        addNewUser('IsHqUser', val, true);
                      }}
                      data-testid="manage-user-hq-user"
                    />
                  </div>
                )}
              </div>
              {useProfile?.profile?.firms?.length === 0 && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    Add a firm below to set defaults.
                  </Typography>
                </>
              )}
            </Grid>
            <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
              <Grid item sm={6}>
                <AgentNetDropdownSelector
                  fullWidth
                  variant="outlined"
                  label={'Firm'}
                  name="AssociatedFirmId"
                  value={userId && userId > 0 ? defaultFirmId : firmTitleValue}
                  required
                  disabled={
                    !hasFirmOptions ||
                    (userId && userId > 0 ? (isHqUser ? true : useProfile.profile?.roleId !== 3) : addUser.IsHqUser)
                  }
                  data-testid="firmName"
                  options={availableFirmOptions}
                  menuOption={(value: number) => {
                    addNewUser('FirmName', value, true);
                    setFirmTitle(value);
                    addNewUser('AssociatedFirmId', value, true);
                    setOfficeName(value);
                    setDefaultFirmId(value);
                    setFirmId(value);
                    const isEcrowFirm = selectedOptions.find((option) => option.id === value);
                    if (isEcrowFirm?.isTitleEscrow === true) {
                      setDefaultRoleValue('');
                      setRole('');
                      setuserRoleOptions(titleEscrowRoles);
                      addNewUser('Role', '');
                    } else {
                      setDefaultRoleValue('Agent');
                      setRole('Agent');
                      setuserRoleOptions(AgentRole);
                      addNewUser('Role', 'Agent');
                    }
                  }}
                  errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
                  showValidation={shouldShowValidation}
                />
              </Grid>

              <Grid item sm={6}>
                <AgentNetDropdownSelector
                  fullWidth
                  variant="outlined"
                  label={'Office'}
                  name="AssociatedOfficeId"
                  value={userId && userId > 0 ? selectedOffice : OfficeValue}
                  required
                  disabled={!hasOfficeOptions || useProfile?.profile?.roleId === 13}
                  menuOption={(value: number) => {
                    addNewUser('AssociatedOfficeId', value, true);
                    setSelectedOffice(value);
                    setOfficeValue(value);
                  }}
                  options={officeNameArray}
                  errs={userId && userId > 0 ? updateUserValidationErrors : validationErrors}
                  showValidation={shouldShowValidation}
                />
              </Grid>
            </Grid>
            {(!(userId && userId > 0) || useProfile?.profile?.roleId !== 13) && (
              <Grid container sm={12} className={classes.fieldSectionRight}>
                <AutocompleteChecklist
                  isAgentNetAdmin={isAgentNetAdmin}
                  options={[
                    { id: 1, name: 'Option 1' },
                    { id: 2, name: 'Option 2' },
                    { id: 3, name: 'Option 3' },
                  ]}
                  label="Firm"
                  sectionTitle="Firm Access"
                  onChange={handleChange}
                  onDelete={handleDelete}
                  value={selectedOptions}
                  qaBtnprefix="autocomplete-checklist-btn"
                />
              </Grid>
            )}
          </>
        )}
        {renderActivityRights()}
        {!hasPermission?.orderManagementAdmin && (
          <Grid item sm={12} className={classes.fieldSectionLeft}>
            <Typography variant="h2">Notes</Typography>
          </Grid>
        )}
        {!hasPermission?.orderManagementAdmin && (
          <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
            <Grid item sm={12}>
              <AgentNetTextInput
                disabled={disableFeature}
                className={classes.notes}
                variant="outlined"
                label="Notes"
                name="notes"
                value={userId && userId > 0 ? notes : addUser.Notes}
                multiline
                fullWidth
                required={false}
                onChange={(event: any) => {
                  addNewUser('Notes', event.target.value, true);
                  setNotes(event.target.value);
                }}
                errs={validationErrors}
                showValidation={shouldShowValidation}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      {showSaveBar && (
        <BottomAppBar
          primaryActionLabel="Save"
          cancelDataQa={'UserProfileCancel'}
          cancelLabel="Cancel"
          primaryActionDataQa={'UserProfileSave'}
          primaryButtonLoader={executeSetFscTeamStatus === AsyncStatus.PENDING}
          onPrimaryClick={isSaved || (userId && userId > 0) ? handleUpdate : handleSave}
          disablePrimary={
            userId && userId > 0 && useProfile?.profile?.roleId === 13 && adminAccessRoles.includes(role)
              ? false
              : HasUserAndIsAdminAccessRoleExceptOrderManagementAdminActivityRight
              ? true
              : executeSetFscTeamStatus === AsyncStatus.PENDING
              ? true
              : isSave
          }
          onCancel={() => {
            if (isSaved) {
              userId = addUserResponseValue.UserId;
            }
            if (userId && userId > 0) {
              if (userId && reLoadEdit) {
                reLoadEdit(userId);
              }
            } else {
              window.location.reload();
            }
          }}
          showFullWidth={useProfile?.profile?.roleId == 13 || hasPermission?.orderManagementAdmin}
        />
      )}
      <UnsavedChangesDialog
        open={navigationGuard.isOpen}
        saving={navigationGuard.isSaving}
        onSave={navigationGuard.saveAndProceed}
        onDiscard={navigationGuard.discardAndProceed}
        onStay={navigationGuard.stay}
      />
    </>
  );
};

export default AddNewUser;
